import { Checkbox, Popconfirm, Select, SelectProps } from "antd"
import { DefaultOptionType } from "antd/es/select"
import debounce from "lodash-es/debounce"
import isEqual from "lodash-es/isEqual"
import { ChevronDownIcon } from "lucide-react"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Virtuoso } from "react-virtuoso"

const CheckboxItem = memo(function CheckboxItem({
  option,
  checked,
  disabled,
  onChange
}: {
  option: DefaultOptionType
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean, value: string) => void
}) {
  return (
    <Checkbox
      key={option.value}
      disabled={disabled}
      checked={checked}
      onChange={(e) => onChange(e.target.checked, option.value as string)}
      className="w-full !text-xs !font-normal py-1 px-3 dark:hover:!bg-dark-2 hover:bg-[#F5F5F5] min-h-8 items-center rounded-sm"
    >
      {option.label}
    </Checkbox>
  )
})

function CheckboxSelect(
  props: SelectProps & {
    maxErrorKeyword?: string
    onBlur?: (value: string[]) => void
    showSelectAll?: boolean
  }
) {
  const { t } = useTranslation()
  const {
    value,
    options: propOptions,
    onChange,
    maxCount,
    maxErrorKeyword = "options",
    className,
    onSearch,
    onBlur,
    defaultValue,
    showSelectAll,
    classNames,
    suffixIcon = !propOptions && showSelectAll && (
      <ChevronDownIcon className="w-4 h-4 dark:text-dark-6 text-dark" />
    ),
    ...rest
  } = props

  const [visible, setVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedValues, setSelectedValues] = useState<string[]>(
    () => value || defaultValue || []
  )

  useEffect(() => {
    if (value) {
      setSelectedValues((prev) => (isEqual(value, prev) ? prev : value))
    }
  }, [value])

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])

  const filteredOptions = useMemo(() => {
    if (!propOptions) return []
    if (!searchTerm) return propOptions

    const lower = searchTerm.toLowerCase()
    return propOptions.filter(
      (o) =>
        o.label?.toString().toLowerCase().includes(lower) ||
        o.value?.toString().toLowerCase().includes(lower)
    )
  }, [propOptions, searchTerm])

  const debouncedSetSearch = useMemo(
    () =>
      debounce((t: string) => {
        setSearchTerm(t)
        onSearch?.(t)
      }, 250),
    [onSearch]
  )

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  const handleSearch = useCallback(
    (text: string) => {
      debouncedSetSearch(text)
    },
    [debouncedSetSearch]
  )

  const handleDropdownVisibleChange = useCallback((open: boolean) => {
    setVisible(open)
    setSearchTerm("")
  }, [])

  const handleCheckboxChange = useCallback(
    (checked: boolean, optionValue: string) => {
      setSelectedValues((prevSelected) => {
        const newValue = checked
          ? optionValue === "all"
            ? [optionValue]
            : [...prevSelected, optionValue]
          : prevSelected.filter((val) => val !== optionValue)

        onChange?.(newValue)
        return newValue
      })
      setSearchTerm("")
    },
    [onChange]
  )

  const handleDeselect = useCallback(
    (val: string) => {
      setSelectedValues((s) => {
        const newValues = s.filter((v) => v !== val)
        onChange?.(newValues)
        props?.onDeselect?.(val, newValues)
        return newValues
      })
      setSearchTerm("")
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, props.onDeselect]
  )

  const handleSelectAll = useCallback(() => {
    const allValues = (propOptions ?? [])
      .filter((o) => o.value !== "all")
      .map((o) => o.value as string)
    setSelectedValues(allValues)
    onChange?.(allValues)
  }, [propOptions, onChange])

  const handleSelectNone = useCallback(() => {
    setSelectedValues([])
    onChange?.([])
  }, [onChange])

  const dropdownHeader = useMemo(
    () =>
      showSelectAll && (
        <div className="flex gap-1 px-3 py-1 sticky top-0 bg-genesis dark:bg-dark-2 border-b border-light-border dark:border-border">
          <button
            className="text-xs text-primary hover:opacity-70"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSelectAll}
          >
            {t("label_checkbox_select_all")}
          </button>
          <span className="text-xs text-gray-400">/</span>

          <Popconfirm
            title={t("label_checkbox_remove_all_title")}
            description={
              <p
                className="text-xs"
                dangerouslySetInnerHTML={{
                  __html: t("label_checkbox_remove_all_description")
                }}
              />
            }
            okText={t("label_checkbox_remove")}
            cancelText={t("label_cancel")}
            onConfirm={handleSelectNone}
            placement="bottomLeft"
            arrow={false}
            cancelButtonProps={{
              className: "text-xs"
            }}
            okButtonProps={{
              className: "text-xs"
            }}
          >
            <button
              className="text-xs text-primary hover:opacity-70"
              onMouseDown={(e) => e.preventDefault()}
            >
              {t("label_checkbox_remove_all")}
            </button>
          </Popconfirm>
        </div>
      ),
    [showSelectAll, handleSelectAll, handleSelectNone, t]
  )

  const renderOptions = useMemo(() => {
    if (!filteredOptions.length) {
      return (
        <div className="p-2 text-center text-gray-500">
          {t("components.label_no_options_available")}
        </div>
      )
    }

    const virtuosoHeight = Math.min(
      260,
      Math.max(filteredOptions.length * 30, 30)
    )

    return (
      <Virtuoso
        style={{ height: virtuosoHeight }}
        totalCount={filteredOptions.length}
        fixedItemHeight={30}
        overscan={4}
        initialItemCount={Math.min(6, filteredOptions.length)}
        increaseViewportBy={80}
        itemContent={(index) => {
          const option = filteredOptions[index]
          const isSelected = selectedSet.has(option?.value as string)
          const isAllSelected = selectedSet.has("all") && option.value !== "all"
          const isDisabled =
            (maxCount && selectedSet.size >= maxCount && !isSelected) ||
            isAllSelected

          return (
            <CheckboxItem
              option={option}
              checked={isSelected}
              disabled={!!isDisabled}
              onChange={handleCheckboxChange}
            />
          )
        }}
      />
    )
  }, [filteredOptions, maxCount, handleCheckboxChange, selectedSet, t])

  const dropdownFooter = useMemo(
    () =>
      maxCount &&
      propOptions &&
      propOptions?.length >= maxCount && (
        <div className="flex justify-center items-center p-1 sticky bottom-0 bg-genesis dark:bg-dark-2">
          <p className="text-13 text-red-500">
            {selectedValues.length >= maxCount
              ? t("label_checkbox_maximum_selected", {
                  keyword: maxErrorKeyword
                })
              : t("label_checkbox_please_select_max", {
                  count: maxCount,
                  keyword: maxErrorKeyword
                })}
          </p>
        </div>
      ),
    [maxCount, maxErrorKeyword, selectedValues.length, propOptions, t]
  )

  const onBlurFn = useCallback(() => {
    if (!visible) {
      onBlur?.(selectedValues)
    }
  }, [onBlur, selectedValues, visible])

  return (
    <Select
      {...rest}
      open={visible}
      options={propOptions}
      value={selectedValues}
      virtual
      onSearch={handleSearch}
      filterOption={false}
      onOpenChange={handleDropdownVisibleChange}
      autoClearSearchValue={false}
      showSearch
      onDeselect={handleDeselect}
      // The inner Select is fully controlled via `value={selectedValues}`
      // and never receives the external `onChange` directly (all mutations
      // go through handleCheckboxChange/handleDeselect/handleSelectAll
      // instead) — without this, clicking the native clear (×) icon fires
      // antd's internal clear with nowhere to go, and the controlled value
      // snaps right back.
      onClear={handleSelectNone}
      suffixIcon={suffixIcon}
      className={className}
      classNames={{
        ...classNames,
        popup: {
          ...classNames?.popup,
          root:
            classNames?.popup?.root ||
            "dark:bg-dark-1 border border-light-border dark:border-border"
        }
      }}
      popupRender={() => (
        <div className="flex flex-col gap-1 max-h-[260px] overflow-y-auto">
          {dropdownHeader}
          {renderOptions}
          {dropdownFooter}
        </div>
      )}
      onBlur={onBlurFn}
    />
  )
}

export default memo(CheckboxSelect)
CheckboxSelect.displayName = "CheckboxSelect"
